import {
  ScaffolderFormPaper,
  ScaffolderInputHeader,
} from '@internal/plugin-scaffolder-common-field-react';
import React, { useMemo, useState, useEffect, useCallback } from 'react';

import { EntityRelation } from '@backstage/catalog-model';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

import {
  Box,
  MenuItem,
  TextField,
  Typography,
  FormHelperText,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { useGetDaasEntities } from '../../hooks/useGetDaasEntities/useGetDaasEntities';
import { DaasComponentPickerConfig, COMPONENT_TYPES } from './types';
import {
  convertEntityToApplication,
  convertEntityToApplicationSet,
  convertEntityToProject,
} from './utils';

const useStyles = makeStyles(theme => ({
  inputContainer: {
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1),
    width: '60%',
  },
}));

export const DaasComponentPicker = (
  props: FieldExtensionComponentProps<DaasComponentPickerConfig>,
) => {
  const {
    onChange,
    schema: { title = 'DaaS Component', description = '' },
    required,
    rawErrors,
    formData,
    disabled,
    formContext,
    uiSchema,
  } = props;

  const classes = useStyles();

  const presetComponentType = useMemo(
    () => uiSchema?.['ui:options']?.componentType as string | undefined,
    [uiSchema],
  );
  const fillEditFormData = useMemo(
    () => uiSchema?.['ui:options']?.fillEditFormData as boolean | undefined,
    [uiSchema],
  );
  const relationsValidation = useMemo(
    () => uiSchema?.['ui:options']?.relationsValidation as boolean | undefined,
    [uiSchema],
  );
  const userOwedComponents = useMemo(
    () => Boolean(uiSchema?.['ui:options']?.userOwedComponents),
    [uiSchema],
  );
  const userInSecurityGroup = useMemo(
    () => Boolean(uiSchema?.['ui:options']?.userInSecurityGroup),
    [uiSchema],
  );

  const [selectedType, setSelectedType] = useState<string>(
    formData?.type || presetComponentType || COMPONENT_TYPES.APPLICATION,
  );
  const [selectedComponent, setSelectedComponent] = useState<Entity | null>(
    null,
  );
  const [prefilledEntityRefNotFound, setPrefilledEntityRefNotFound] =
    useState(false);
  const [invalidPrefilledEntityRef, setInvalidPrefilledEntityRef] = useState<
    string | null
  >(null);

  const { value: components = [], loading } = useGetDaasEntities(
    selectedType,
    userOwedComponents,
    userInSecurityGroup,
  );

  const syncEntityDataToForm = useCallback(
    (entity: Entity) => {
      if (fillEditFormData) {
        switch (selectedType) {
          case COMPONENT_TYPES.APPLICATION: {
            const convertedData = convertEntityToApplication(entity);
            formContext.formData.applicationName =
              convertedData.applicationName;
            formContext.formData.description = convertedData.description;
            formContext.formData.owner = convertedData.owner;
            formContext.formData.project = convertedData.project;
            formContext.formData.syncPolicy = convertedData.syncPolicy;
            formContext.formData.applicationMetadata =
              convertedData.applicationMetadata;
            break;
          }
          case COMPONENT_TYPES.APPLICATION_SET: {
            const convertedData = convertEntityToApplicationSet(entity);
            formContext.formData.applicationSetName =
              convertedData.applicationSetName;
            formContext.formData.description = convertedData.description;
            formContext.formData.owner = convertedData.owner;
            formContext.formData.generators = convertedData.generators;
            formContext.formData.project = convertedData.project;
            formContext.formData.syncPolicy = convertedData.syncPolicy;
            formContext.formData.applicationMetadata =
              convertedData.applicationMetadata;
            break;
          }
          case COMPONENT_TYPES.PROJECT: {
            const convertedData = convertEntityToProject(entity);
            formContext.formData.projectName = convertedData.projectName;
            formContext.formData.description = convertedData.description;
            formContext.formData.owner = convertedData.owner;
            formContext.formData.securityGroups = convertedData.securityGroups;
            formContext.formData.clusters = convertedData.clusters;
            formContext.formData.notificationChannel =
              convertedData.notificationChannel;
            formContext.formData.notificationServices =
              convertedData.notificationServices;
            break;
          }
          default:
            break;
        }
      }
    },
    [fillEditFormData, selectedType, formContext],
  );

  useEffect(() => {
    if (formData?.entityRef && components.length > 0 && !selectedComponent) {
      const component = components.find(
        c => stringifyEntityRef(c) === formData.entityRef,
      );
      if (component) {
        setPrefilledEntityRefNotFound(false);
        setInvalidPrefilledEntityRef(null);
        setSelectedComponent(component);
        onChange({
          type: selectedType,
          entityRef: stringifyEntityRef(component),
          relationsValidation: relationsValidation || false,
          dependencyOf:
            component.relations
              ?.filter(
                (relation: EntityRelation) => relation.type === 'dependencyOf',
              )
              .map((relation: EntityRelation) => relation.targetRef) || [],
        });
        syncEntityDataToForm(component);
      } else {
        setPrefilledEntityRefNotFound(true);
        setInvalidPrefilledEntityRef(formData.entityRef);
        onChange({
          type: selectedType,
          entityRef: '',
          relationsValidation: relationsValidation || false,
          dependencyOf: [],
        });
      }
    }
  }, [
    formData?.entityRef,
    components,
    selectedComponent,
    selectedType,
    relationsValidation,
    onChange,
    syncEntityDataToForm,
  ]);

  const handleTypeChange = (value: string) => {
    setSelectedType(value);
    setSelectedComponent(null);
    setPrefilledEntityRefNotFound(false);
    setInvalidPrefilledEntityRef(null);
    onChange({
      type: value,
      entityRef: '',
      relationsValidation: relationsValidation || false,
      dependencyOf: [],
    });
  };

  const handleComponentSelect = (_: any, value: Entity | null) => {
    setSelectedComponent(value);
    setPrefilledEntityRefNotFound(false);
    setInvalidPrefilledEntityRef(null);
    onChange({
      type: selectedType,
      entityRef: value ? stringifyEntityRef(value) : '',
      relationsValidation: relationsValidation || false,
      dependencyOf:
        value?.relations
          ?.filter(relation => relation.type === 'dependencyOf')
          .map(relation => relation.targetRef) || [],
    });
    if (value) {
      syncEntityDataToForm(value);
    }
  };

  return (
    <Box>
      <ScaffolderFormPaper>
        <Box>
          <ScaffolderInputHeader title={title} description={description} />
          {!presetComponentType && (
            <Box className={classes.inputContainer}>
              <TextField
                label="Component Type"
                variant="outlined"
                margin="none"
                size="small"
                fullWidth
                required={required}
                select
                value={selectedType}
                onChange={e => handleTypeChange(e.target.value)}
              >
                <MenuItem value={COMPONENT_TYPES.APPLICATION}>
                  Application
                </MenuItem>
                <MenuItem value={COMPONENT_TYPES.APPLICATION_SET}>
                  Application Set
                </MenuItem>
                <MenuItem value={COMPONENT_TYPES.PROJECT}>Project</MenuItem>
              </TextField>
              <FormHelperText>Select the component type</FormHelperText>
            </Box>
          )}
          <Box className={classes.inputContainer}>
            <Autocomplete
              size="small"
              value={selectedComponent}
              loading={loading}
              onChange={handleComponentSelect}
              options={components}
              disabled={
                !selectedType ||
                (!prefilledEntityRefNotFound && Boolean(disabled))
              }
              getOptionLabel={option => {
                if (!option) return '';
                return option.metadata?.title || option.metadata?.name || '';
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Component Name"
                  margin="none"
                  variant="outlined"
                  required={required}
                  placeholder={
                    selectedType ? 'Select a component' : 'Select a type first'
                  }
                  InputProps={params.InputProps}
                  error={
                    prefilledEntityRefNotFound ||
                    (Boolean(rawErrors?.length > 0) && !formData?.entityRef)
                  }
                  helperText={
                    prefilledEntityRefNotFound
                      ? `The component ${
                          invalidPrefilledEntityRef || ''
                        } is not available for editing. Please select another component.`
                      : undefined
                  }
                />
              )}
              renderOption={option => (
                <Box>
                  <Typography variant="body2" noWrap>
                    {option.metadata?.title || option.metadata?.name}
                  </Typography>
                  {option.metadata?.name && option.metadata?.title && (
                    <Typography variant="caption" noWrap color="textSecondary">
                      {option.metadata.name}
                    </Typography>
                  )}
                </Box>
              )}
              noOptionsText={
                selectedType
                  ? 'No DaaS components found'
                  : 'Select a type first'
              }
              loadingText="Loading..."
            />
          </Box>
          {selectedComponent &&
            (selectedType === COMPONENT_TYPES.APPLICATION ||
              selectedType === COMPONENT_TYPES.APPLICATION_SET) && (
              <Box className={classes.inputContainer}>
                <Alert severity="warning">
                  {selectedType === COMPONENT_TYPES.APPLICATION ? (
                    <>
                      Deleting this component will also delete the Kubernetes
                      resources managed by the ArgoCD Application{' '}
                      <strong>
                        <code>
                          {selectedComponent.metadata?.title ||
                            selectedComponent.metadata?.name}
                        </code>
                      </strong>
                      .
                    </>
                  ) : (
                    <>
                      Deleting this component will also delete every Application
                      generated by the ArgoCD ApplicationSet{' '}
                      <strong>
                        <code>
                          {selectedComponent.metadata?.title ||
                            selectedComponent.metadata?.name}
                        </code>
                      </strong>
                      , along with the Kubernetes resources those Applications
                      manage.
                    </>
                  )}
                </Alert>
              </Box>
            )}
        </Box>
      </ScaffolderFormPaper>
    </Box>
  );
};
